import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "434",
  "slug": "what-is-the-coriolis-effect",
  "title": "What Is the Coriolis Effect?",
  "excerpt": "Why do hurricanes spin, why do winds curve, and why do long-range projectiles drift sideways? They all trace back to one consequence of living on a spinning planet.",
  "date": "2026-06-14",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxodXJyaWNhbmUlMjBmcm9tJTIwc3BhY2V8ZW58MXwwfHx8MTc4MTQyNTA3N3ww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The Coriolis effect is the apparent sideways deflection of anything moving across the Earth's surface, caused by the planet's rotation. It bends moving air and water to the right in the Northern Hemisphere and to the left in the Southern Hemisphere, and it is what makes large weather systems spin.",
  "content": [
    {
      "type": "paragraph",
      "text": "Stand still and the ground feels perfectly solid and stationary. But you are actually riding a planet that spins once a day, and at the equator that means hurtling eastward at over 1,600 kilometers per hour. We do not feel it because everything around us moves at the same speed. The moment something travels a long distance across that spinning surface, though, the rotation starts to show, and that is the Coriolis effect."
    },
    {
      "type": "heading",
      "text": "A simple way to picture it"
    },
    {
      "type": "paragraph",
      "text": "Imagine standing at the North Pole and rolling a ball straight toward a friend standing on the equator. While the ball is in flight, the Earth rotates beneath it. By the time the ball reaches the latitude where your friend stood, your friend has been carried eastward, and the ball appears to have curved to the right. Nothing pushed it sideways. The ground simply moved underneath it."
    },
    {
      "type": "paragraph",
      "text": "That apparent curve is the Coriolis effect. It is not a real force in the everyday sense; it is what happens when you track motion from the point of view of a rotating planet rather than from out in space."
    },
    {
      "type": "heading",
      "text": "The two rules that matter"
    },
    {
      "type": "list",
      "items": [
        "In the Northern Hemisphere, moving air and water are deflected to the right of their direction of travel.",
        "In the Southern Hemisphere, they are deflected to the left.",
        "The effect is strongest at the poles and fades to zero exactly at the equator.",
        "It only becomes significant over long distances and long travel times, which is why it shapes weather systems and ocean currents but not short, quick movements."
      ]
    },
    {
      "type": "heading",
      "text": "Why hurricanes spin"
    },
    {
      "type": "paragraph",
      "text": "The most dramatic result is in the weather. Air rushes toward low-pressure zones from all directions, but the Coriolis effect bends each incoming stream sideways. Instead of flowing straight in, the air spirals. In the Northern Hemisphere that spiral turns counterclockwise; in the Southern Hemisphere it turns clockwise. This is why hurricanes, typhoons, and large storm systems rotate, and why their direction of spin depends on which hemisphere they form in."
    },
    {
      "type": "paragraph",
      "text": "The same effect steers the great wind belts, like the trade winds and the westerlies, and helps drive the looping circulation of the major ocean currents. It even matters to long-range artillery and rockets, whose trajectories must be corrected for the drift."
    },
    {
      "type": "heading",
      "text": "The toilet myth"
    },
    {
      "type": "paragraph",
      "text": "You may have heard that water spins down a drain one way in the Northern Hemisphere and the other way in the Southern. That is a myth. At the scale of a sink or toilet, the Coriolis effect is far too weak to matter. The direction your water swirls is decided by the shape of the basin, the position of the jets, and how the water was already moving, not by which hemisphere you are standing in."
    },
    {
      "type": "heading",
      "text": "Where the idea came from"
    },
    {
      "type": "paragraph",
      "text": "The effect is named after Gaspard-Gustave de Coriolis, a French mathematician and engineer who described the underlying mathematics in the 1830s while studying energy in rotating machines like waterwheels. It took decades for scientists to fully connect his equations to the behavior of the atmosphere and oceans, but today the Coriolis effect is a cornerstone of how we understand global weather and climate."
    },
    {
      "type": "heading",
      "text": "Why scale is everything"
    },
    {
      "type": "paragraph",
      "text": "The single most important thing to understand about the Coriolis effect is that it depends on scale. Over a short distance, like the width of a room or even a city, the deflection is so tiny it is completely swamped by other forces and is impossible to notice. Over hundreds or thousands of kilometers, and over hours of travel, those tiny nudges accumulate into something powerful enough to organize an entire hurricane. That is why the effect rules the behavior of vast weather systems and ocean gyres, yet has no measurable say in how your bathwater drains."
    },
    {
      "type": "heading",
      "text": "See it shape the planet"
    },
    {
      "type": "paragraph",
      "text": "Use the Coriolis effect to interpret large-scale motion, alongside pressure differences and other forces. It is one part of the explanation for circulation, rather than a rule for every curve you see in water or clouds."
    }
  ]
};
